/**
 * @fileoverview CurrentRatioChart - Line chart with threshold at 1.0
 *
 * Purpose: Display current ratio and debt ratio with 1.0 threshold reference.
 * Features polished styling: subtle grid, styled dots, clean reference line.
 *
 * Used by: Review phase liquidity analysis
 */

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import {
  periodAlignedLayout,
  periodIndexAxis,
  type PeriodAlignedChartProps,
} from "./periodAlignedLayout";

const config = {
  currentRatio: { label: "Current Ratio", color: "hsl(var(--chart-2))" },
  debtRatio: { label: "Debt Ratio", color: "hsl(var(--chart-1))" },
} satisfies ChartConfig;

export interface CurrentRatioChartProps extends PeriodAlignedChartProps {
  periods: string[];
  currentRatio: (number | null)[];
  debtRatio: (number | null)[];
}

/** Current and debt ratio lines per period against a 1.0 reference line. */
export function CurrentRatioChart({
  periods,
  currentRatio,
  debtRatio,
  plotInsetLeft,
}: CurrentRatioChartProps) {
  const layout = periodAlignedLayout(plotInsetLeft, 40);
  // Missing values stay null — a bar is skipped and a line breaks — rather
  // than plotting as zero beside a table that shows a dash (MOR-1282 QA).
  const data = periods.map((period, i) => ({
    index: i,
    period,
    currentRatio: currentRatio[i] ?? null,
    debtRatio: debtRatio[i] ?? null,
  }));

  return (
    <ChartContainer config={config} className="min-h-[180px] w-full">
      <LineChart data={data} margin={layout.margin}>
        <CartesianGrid
          vertical={false}
          stroke="hsl(var(--border))"
          strokeOpacity={0.5}
        />
        <XAxis
          // Aligned to a period table, periods sit at band centres like the
          // bar charts beside it rather than at the plot edges (MOR-1282).
          {...(plotInsetLeft == null
            ? { dataKey: "period" }
            : periodIndexAxis(periods))}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          width={layout.yAxisWidth}
        />
        <ReferenceLine
          y={1}
          stroke="hsl(var(--destructive))"
          strokeDasharray="6 4"
          strokeOpacity={0.5}
          label={{
            value: "1.0",
            position: "insideTopRight",
            fill: "hsl(var(--muted-foreground))",
            fontSize: 10,
          }}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) => {
                const row: unknown = payload[0]?.payload;
                return typeof row === "object" &&
                  row !== null &&
                  "period" in row &&
                  typeof row.period === "string"
                  ? row.period
                  : null;
              }}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Line
          type="monotone"
          dataKey="currentRatio"
          stroke="hsl(var(--chart-2))"
          strokeWidth={2}
          dot={{
            r: 3,
            fill: "hsl(var(--chart-2))",
            strokeWidth: 2,
            stroke: "hsl(var(--background))",
          }}
          activeDot={{ r: 5, strokeWidth: 2 }}
        />
        <Line
          type="monotone"
          dataKey="debtRatio"
          stroke="hsl(var(--chart-1))"
          strokeWidth={2}
          dot={{
            r: 3,
            fill: "hsl(var(--chart-1))",
            strokeWidth: 2,
            stroke: "hsl(var(--background))",
          }}
          activeDot={{ r: 5, strokeWidth: 2 }}
        />
      </LineChart>
    </ChartContainer>
  );
}
